import { useState, useEffect, useRef, useCallback } from 'react';
import type { ToolCall } from '../../store/chat';

const TOOL_MESSAGES: Record<string, string> = {
  search_nodes: 'Searching for the right nodes',
  get_node_schema: 'Reading node configuration',
  get_skill: 'Applying best practices',
  create_plan: 'Drafting your workflow plan',
  ask_user_questions: 'Preparing a few questions',
};

const AMBIENT_PLANNING = [
  'Analyzing your request',
  'Thinking through the ideal workflow',
  'Identifying the right approach',
  'Exploring available integrations',
  'Evaluating the best node combinations',
  'Mapping out the automation logic',
  'Reviewing trigger options',
  'Considering error handling',
  'Checking for the best integrations',
  'Planning the data flow',
  'Looking at connection patterns',
  'Reviewing node compatibility',
];

const AMBIENT_BUILDING = [
  'Building your workflow',
  'Assembling nodes and connections',
  'Configuring node parameters',
  'Wiring up the data flow',
  'Finalizing the workflow structure',
  'Setting up trigger conditions',
  'Mapping fields between nodes',
  'Adding authentication config',
  'Connecting the outputs',
  'Applying node settings',
  'Laying out the workflow',
  'Polishing the final details',
];

function DotLoader() {
  return (
    <div className="flex items-center gap-1">
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          className="h-1.5 w-1.5 rounded-full bg-primary-400 animate-dot-bounce"
          style={{ animationDelay: `${i * 150}ms` }}
        />
      ))}
    </div>
  );
}

interface StreamingStatusProps {
  phase: 'planning' | 'building';
  toolCalls: ToolCall[];
  statusMessage: string | null;
}

export function StreamingStatus({ phase, toolCalls, statusMessage }: StreamingStatusProps) {
  const [displayText, setDisplayText] = useState('');
  const [prevText, setPrevText] = useState('');
  const [isTransitioning, setIsTransitioning] = useState(false);
  const ambientIndex = useRef(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const displayTextRef = useRef('');
  const lastToolCountRef = useRef(0);

  // Keep ref in sync with state
  useEffect(() => {
    displayTextRef.current = displayText;
  }, [displayText]);

  const transitionTo = useCallback((next: string) => {
    const current = displayTextRef.current;
    if (next === current) return;
    setPrevText(current);
    setDisplayText(next);
    setIsTransitioning(true);
    setTimeout(() => setIsTransitioning(false), 400);
  }, []);

  // React to tool calls — show message for the most recent tool (running OR just completed)
  // Tool start+complete often arrive in the same SSE batch, so we track by count changes
  useEffect(() => {
    if (toolCalls.length === 0) return;
    if (toolCalls.length === lastToolCountRef.current) return;
    lastToolCountRef.current = toolCalls.length;

    // Show message for the latest tool call
    const latest = toolCalls[toolCalls.length - 1];
    if (latest) {
      const msg = TOOL_MESSAGES[latest.tool];
      if (msg) transitionTo(msg);
    }
  }, [toolCalls, transitionTo]);

  // React to meaningful server status messages
  useEffect(() => {
    if (
      statusMessage &&
      statusMessage !== 'Thinking...' &&
      statusMessage !== 'Building workflow...'
    ) {
      transitionTo(statusMessage);
    }
  }, [statusMessage, transitionTo]);

  // Set initial ambient message and cycle
  useEffect(() => {
    const source = phase === 'building' ? AMBIENT_BUILDING : AMBIENT_PLANNING;
    // Shuffle so it feels different each time
    const ambientMessages = [...source].sort(() => Math.random() - 0.5);

    // Set initial message if nothing is showing
    if (!displayTextRef.current) {
      const initial = ambientMessages[0] ?? '';
      setDisplayText(initial);
      displayTextRef.current = initial;
      ambientIndex.current = 0;
    }

    timerRef.current = setInterval(() => {
      ambientIndex.current = (ambientIndex.current + 1) % ambientMessages.length;
      const next = ambientMessages[ambientIndex.current];
      if (next) transitionTo(next);
    }, 2500);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [phase, transitionTo]);

  return (
    <div className="flex items-center gap-3 py-2">
      <DotLoader />
      <div className="relative h-5 flex-1 overflow-hidden">
        {/* Previous text — rolls up and out */}
        {isTransitioning && prevText && (
          <span className="absolute inset-0 flex items-center text-sm text-gray-500 animate-roll-out">
            {prevText}
          </span>
        )}
        {/* Current text — rolls up from below */}
        <span
          className={`absolute inset-0 flex items-center text-sm text-gray-500 ${
            isTransitioning ? 'animate-roll-in' : ''
          }`}
        >
          {displayText}
        </span>
      </div>
    </div>
  );
}
